<template>
    <v-container fluid>
        <!-- Page Header -->
        <v-card class="mb-4">
            <v-card-title>
                <router-link :to="{ name: 'admin-infotv-screens' }" class="mr-2">
                    <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
                </router-link>
                {{ $t('Näytön sisällönhallinta') }}
            </v-card-title>
            <v-card-subtitle>
                {{ $t('Asettelu') }}: {{ layoutName }}
            </v-card-subtitle>
        </v-card>

        <!-- Loading State -->
        <v-card v-if="loading && !layout">
            <v-card-text class="text-center py-12">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </v-card-text>
        </v-card>

        <!-- Split Layout: 2 Columns -->
        <v-row v-else-if="layout === 2">
            <v-col cols="12" lg="8">
                <ContentList :title="$t('Pääsisältö (70%)')" :items="primaryContent" :position="1" :screen-id="screen"
                    :loading="loading" :news-preview="newsPreview" @reorder="handleReorder" @remove="handleRemove"
                    @upload="openUploadDialog(1)" @addFromLibrary="openLibraryModal(1)" @addNews="addNewsContent(1)"
                    @addEvents="addEventsContent(1)" />
            </v-col>
            <v-col cols="12" lg="4">
                <ContentList :title="$t('Toissijainen sisältö (30%)')" :items="secondaryContent" :position="2"
                    :screen-id="screen" :loading="loading" :news-preview="newsPreview" @reorder="handleReorder"
                    @remove="handleRemove" @upload="openUploadDialog(2)" @addFromLibrary="openLibraryModal(2)"
                    @addNews="addNewsContent(2)" @addEvents="addEventsContent(2)" />
            </v-col>
        </v-row>

        <!-- Full Layout: 1 Column -->
        <v-row v-else>
            <v-col cols="12">
                <ContentList :title="$t('Pääsisältö')" :items="primaryContent" :position="1" :screen-id="screen"
                    :loading="loading" :news-preview="newsPreview" @reorder="handleReorder" @remove="handleRemove"
                    @upload="openUploadDialog(1)" @addFromLibrary="openLibraryModal(1)" @addNews="addNewsContent(1)"
                    @addEvents="addEventsContent(1)" />
            </v-col>
        </v-row>

        <!-- Footer Texts Section -->
        <v-row>
            <v-col cols="12">
                <FooterTextsList :items="footerTexts" :screen-id="screen" :loading="footerLoading"
                    @add="handleAddFooterText" @remove="handleRemoveFooterText" @reorder="handleReorderFooterTexts" />
            </v-col>
        </v-row>

        <!-- Upload Dialog -->
        <UploadDialog v-model="uploadDialog" :screen-id="screen" :position="uploadPosition" @uploaded="fetchContent" />

        <!-- Library Modal -->
        <LibraryModal v-model="libraryModal" :screen-id="screen" :position="libraryPosition" @added="fetchContent" />
    </v-container>
</template>

<script>
import { onMounted, ref, computed } from '@vue/composition-api'
import adminApi from '@/api/admin'
import ContentList from './components/ContentList.vue'
import UploadDialog from './components/UploadDialog.vue'
import LibraryModal from './components/LibraryModal.vue'
import FooterTextsList from './components/FooterTextsList.vue'
import { useTranslations } from '@/@core/libs/i18n'
import { i18n } from '@/plugins/i18n'
import { mdiArrowLeft } from '@mdi/js'


export default {
    name: 'ScreenContent',
    components: {
        ContentList,
        UploadDialog,
        LibraryModal,
        FooterTextsList,
    },
    props: {
        screen: {
            type: [String, Number],
            required: true
        }
    },
    setup(props) {
        // Layout constants
        const LAYOUT_FULL = 1
        const LAYOUT_SPLIT = 2

        // State
        const layout = ref(null)
        const primaryContent = ref([])
        const secondaryContent = ref([])
        const loading = ref(false)

        // News preview state
        const newsPreview = ref([])

        // Footer texts state
        const footerTexts = ref([])
        const footerLoading = ref(false)

        // Dialog states
        const uploadDialog = ref(false)
        const uploadPosition = ref(1)
        const libraryModal = ref(false)
        const libraryPosition = ref(1)


        // Computed
        const layoutName = computed(() => {
            if (layout.value === LAYOUT_SPLIT) return i18n.t('Jaettu asettelu (70% / 30%)')
            if (layout.value === LAYOUT_FULL) return i18n.t('Koko leveys')
            return '...'
        })

        // Methods
        const fetchContent = async () => {
            loading.value = true
            try {
                const response = await adminApi.getInfoTvScreenContent(props.screen)
                if (response.ok) {
                    layout.value = response.data.layout
                    primaryContent.value = response.data.primary || []
                    secondaryContent.value = response.data.secondary || []
                }
            } catch (error) {
                console.error('Error fetching content:', error)
            } finally {
                loading.value = false
            }
        }

        const handleReorder = async (position, orderedItems) => {
            // Prepare order data in new format
            const orderData = {
                primary: position === 1 ? orderedItems.map(item => item.id) : primaryContent.value.map(item => item.id),
                secondary: position === 2 ? orderedItems.map(item => item.id) : secondaryContent.value.map(item => item.id)
            }

            try {
                await adminApi.sortInfoTvScreenContent(props.screen, orderData)
                // Update local state
                if (position === 1) {
                    primaryContent.value = orderedItems
                } else {
                    secondaryContent.value = orderedItems
                }
            } catch (error) {
                console.error('Error sorting content:', error)
                // Refetch to restore correct order
                fetchContent()
            }
        }

        const handleRemove = async (contentId) => {
            if (!confirm(i18n.t('Haluatko varmasti poistaa sisällön näytöltä?'))) {
                return
            }

            loading.value = true
            try {
                await adminApi.deleteInfoTvScreenContent(props.screen, contentId)
                await fetchContent()
            } catch (error) {
                console.error('Error removing content:', error)
            } finally {
                loading.value = false
            }
        }

        const openUploadDialog = (position) => {
            uploadPosition.value = position
            uploadDialog.value = true
        }

        const openLibraryModal = (position) => {
            libraryPosition.value = position
            libraryModal.value = true
        }

        const addNewsContent = async (position) => {
            loading.value = true
            try {
                await adminApi.saveInfoTvScreenContent(props.screen, {
                    content_type: 1, // INFOTV_CONTENT_NEWS
                    content_id: null,
                    position: position
                })
                await fetchContent()
            } catch (error) {
                console.error('Error adding news content:', error)
                if (error.response?.data?.error) {
                    alert(error.response.data.error)
                }
            } finally {
                loading.value = false
            }
        }

        const addEventsContent = async (position) => {
            loading.value = true
            try {
                await adminApi.saveInfoTvScreenContent(props.screen, {
                    content_type: 4, // INFOTV_CONTENT_EVENTS
                    content_id: null,
                    position: position
                })
                await fetchContent()
            } catch (error) {
                console.error('Error adding events content:', error)
                if (error.response?.data?.error) {
                    alert(error.response.data.error)
                }
            } finally {
                loading.value = false
            }
        }

        // News Preview Methods
        const fetchNewsPreview = async () => {

            try {
                const response = await adminApi.getInfoTvNewsPreview(props.screen)
                if (response.ok) {
                    newsPreview.value = response.data || []
                }
            } catch (error) {
                console.error('Error fetching news preview:', error)
                newsPreview.value = []
            }
        }

        // Footer Texts Methods
        const fetchFooterTexts = async () => {
            footerLoading.value = true
            try {
                const response = await adminApi.getInfoTvFooterTexts(props.screen)
                if (response.ok) {
                    footerTexts.value = response.data || []
                }
            } catch (error) {
                console.error('Error fetching footer texts:', error)
            } finally {
                footerLoading.value = false
            }
        }

        const handleAddFooterText = async (text) => {
            footerLoading.value = true
            try {
                await adminApi.saveInfoTvFooterText(props.screen, text)
                await fetchFooterTexts()
            } catch (error) {
                console.error('Error adding footer text:', error)
                alert(i18n.t('Virhe lisättäessä tekstiä'))
            } finally {
                footerLoading.value = false
            }
        }

        const handleRemoveFooterText = async (textId) => {

            if (!confirm(i18n.t('Haluatko varmasti poistaa tekstin?'))) {
                return
            }

            footerLoading.value = true
            try {
                await adminApi.deleteInfoTvFooterText(props.screen, textId)
                await fetchFooterTexts()
            } catch (error) {
                console.error('Error removing footer text:', error)
            } finally {
                footerLoading.value = false
            }
        }

        const handleReorderFooterTexts = async (orderedItems) => {
            const textIds = orderedItems.map(item => item.id)
            try {
                await adminApi.sortInfoTvFooterTexts(props.screen, textIds)
                footerTexts.value = orderedItems
            } catch (error) {
                console.error(i18n.t('Error sorting footer texts:', error))
                fetchFooterTexts()
            }
        }

        // Lifecycle
        onMounted(() => {
            fetchContent()
            fetchFooterTexts()
            fetchNewsPreview()
        })

        return {
            layout,
            layoutName,
            primaryContent,
            secondaryContent,
            loading,
            newsPreview,
            footerTexts,
            footerLoading,
            uploadDialog,
            uploadPosition,
            libraryModal,
            libraryPosition,
            handleReorder,
            handleRemove,
            openUploadDialog,
            openLibraryModal,
            addNewsContent,
            addEventsContent,
            fetchContent,
            handleAddFooterText,
            handleRemoveFooterText,
            handleReorderFooterTexts,
            icons: {
                mdiArrowLeft
            }
        }
    }
}
</script>

<style scoped>
/* Add any custom styles here */
</style>
